import { ChevronDown } from 'lucide-react';
import React, { FC, ReactNode, useState } from 'react';

import {
  AnimatedCheckIcon,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  Popover,
  PopoverContent,
  PopoverTrigger,
  TextOverflow,
  cn,
} from '@column/ui';

export interface SearchSelectOption {
  label: string;
  value: string;
  /** Shown after the label in a lighter, regular-weight column; searchable too. */
  description?: string;
  /** Sits before the label, in the list and on the trigger, e.g. a country flag. */
  icon?: ReactNode;
}

interface SearchSelectProps {
  value?: string;
  onChange: (value: string) => void;
  options: SearchSelectOption[];
  placeholder: string;
  searchPlaceholder?: string;
  invalid?: boolean;
  /** Lists the selected option first, split from the rest by a separator. */
  pinSelected?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
}

// The trigger's height (h-10): the popover opens this far up, so its search row covers the trigger exactly.
const TRIGGER_HEIGHT_PX = 40;

// Opacity only, so the popover never looks like it's sliding in from somewhere else.
const FADE_ONLY_MOTION = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
} as const;

const matchOption = (label: string, search: string) => {
  const haystack = label.toLowerCase();
  const needle = search.trim().toLowerCase();
  if (haystack.startsWith(needle)) return 1;
  return haystack.includes(needle) ? 0.5 : 0;
};

// A Select-looking trigger with a searchable list, for option sets too long to scroll.
export const SearchSelect: FC<SearchSelectProps> = ({
  value,
  onChange,
  options,
  placeholder,
  searchPlaceholder = 'Search…',
  invalid,
  pinSelected,
  onOpenChange,
  className,
}) => {
  const [open, setOpen] = useState(false);
  const changeOpen = (next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
  };
  const selected = options.find((option) => option.value === value);
  const pinned = pinSelected && selected ? selected : undefined;
  const rest = pinned ? options.filter((option) => option.value !== pinned.value) : options;
  const hasDescriptions = options.some((option) => option.description);
  // A shared grid lines the descriptions up after the widest visible label.
  const groupClassName = hasDescriptions
    ? '[&_[cmdk-group-items]]:grid [&_[cmdk-group-items]]:grid-cols-[auto_minmax(0,1fr)]'
    : undefined;

  const renderItem = (option: SearchSelectOption) => (
    <CommandItem
      key={option.value}
      value={option.description ? `${option.label} ${option.description}` : option.label}
      onSelect={() => {
        onChange(option.value);
        changeOpen(false);
      }}
      className={cn('pr-8', hasDescriptions && 'col-span-2 grid grid-cols-subgrid gap-x-3')}
    >
      <span className="flex min-w-0 items-center gap-2">
        {option.icon}
        <span className="truncate">{option.label}</span>
      </span>
      {/* Long descriptions truncate; hovering one reveals the rest. */}
      {hasDescriptions && (
        <TextOverflow className="min-w-0 font-normal text-muted-foreground">{option.description}</TextOverflow>
      )}
      <span className="pointer-events-none absolute right-2 flex size-4 items-center justify-center">
        <AnimatedCheckIcon checked={option.value === value} />
      </span>
    </CommandItem>
  );

  return (
    <Popover open={open} onOpenChange={changeOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-invalid={invalid}
          className={cn(
            'flex h-10 w-full cursor-pointer items-center justify-between gap-2 rounded-lg bg-white px-4 text-sm font-medium text-gray-600 shadow-btn-secondary transition-[color,box-shadow,opacity] duration-150 outline-none hover:shadow-btn-secondary-hover focus-visible:shadow-[0_0_0_1px_var(--color-blue-500)] focus-visible:ring-[3px] focus-visible:ring-blue-500/15 aria-invalid:shadow-[0_0_0_1px_var(--color-destructive)] aria-invalid:ring-[3px] aria-invalid:ring-destructive/20 dark:bg-input/30 dark:text-gray-100 dark:shadow-[0_0_0_1px_rgb(255_255_255/0.15)] dark:hover:shadow-[0_0_0_1px_rgb(255_255_255/0.25)] dark:aria-invalid:ring-destructive/40',
            // The popover opens on top of the trigger; hide it so their shadows don't stack.
            'data-[state=open]:opacity-0',
            !selected && 'text-gray-400 dark:text-gray-500',
            className
          )}
        >
          {/* w-0 + flex-1 keeps a long selection out of the button's natural width, so it truncates instead of
              widening the field past its column (FormField's control column is sized to its content). */}
          {selected?.icon}
          <span className="w-0 flex-1 truncate text-left">
            {selected?.label ?? placeholder}
            {selected?.description && (
              <span className="font-normal text-muted-foreground"> {selected.description}</span>
            )}
          </span>
          <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      {/* Opens over the trigger, like the bank account selector: the search takes the trigger's place, so you can
          type straight away without anything moving. It never flips above the trigger; the list shrinks to the space
          below instead (at least 10rem), and scrolls. */}
      <PopoverContent
        align="start"
        sideOffset={-TRIGGER_HEIGHT_PX}
        avoidCollisions={false}
        collisionPadding={16}
        className="flex max-h-[max(10rem,min(var(--radix-popover-content-available-height),22rem))] w-(--radix-popover-trigger-width) min-w-56 flex-col p-0"
        {...FADE_ONLY_MOTION}
      >
        {/* Substring matching instead of cmdk's fuzzy scoring, so typing a code like "GDDS" finds it first. */}
        <Command
          filter={matchOption}
          className="min-h-0 flex-1 bg-transparent [&_[data-slot=command-input-wrapper]]:h-10 [&_[data-slot=command-input-wrapper]]:px-4"
        >
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList className="max-h-none min-h-0 flex-1">
            <CommandEmpty>No results found</CommandEmpty>
            {pinned && (
              <>
                <CommandGroup className={groupClassName}>{renderItem(pinned)}</CommandGroup>
                <CommandSeparator />
              </>
            )}
            <CommandGroup className={groupClassName}>{rest.map(renderItem)}</CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
